import { useState, useMemo, useCallback } from 'react'
import { useTranslation } from "react-i18next"
import styled from 'styled-components'
import { Calendar } from 'antd'
import { Dayjs } from 'dayjs'
import dayjs from 'dayjs'

import { EventStatusEnum } from '../enums'
import { CourseDataQuantityType, EventType, SoftBookingType, PrivateCourseType } from '../types'
import { LabelText, LabelBox } from '../components/base-ui'
import { displayPrice, htmlDecode, isPrivateCourseByName } from '../helpers'
import { WpEnvCityEnum } from '../enums'

interface SelectCourseProps {
  isAdmin: boolean
  isAdminMode: boolean
  eventsData: EventType[]
  listSoftBooking: SoftBookingType[]
  selectedEvent?: EventType
  selectCalendarDate?: Dayjs
  selectedCourses: CourseDataQuantityType[]
  onClickBack: () => void
  onClickBackPrivateRoomBooking: () => void
  onClickNext: (event: EventType) => void
  excludeEventIds?: string[]
  wpEnvCity: keyof typeof WpEnvCityEnum | ''
  listPrivateCourse: PrivateCourseType[]
}

type ConditonProps = 
| {
    isEditBookingMode?: never
    onClickChangeBooking?: never
    isRequestingChangeBooking?: never
  }
| {
    isEditBookingMode: boolean
    onClickChangeBooking: (event: EventType) => void
    isRequestingChangeBooking: boolean
  }

const SelectCourseDate = ({ 
  isAdmin, 
  isAdminMode,
  eventsData, 
  selectedEvent, 
  listSoftBooking,
  selectCalendarDate, 
  selectedCourses, 
  onClickBack, 
  onClickBackPrivateRoomBooking,
  onClickNext, 
  excludeEventIds = [], 
  isEditBookingMode = false, 
  onClickChangeBooking = () => {},
  isRequestingChangeBooking,
  wpEnvCity,
  listPrivateCourse,
}: SelectCourseProps & ConditonProps) => {
  const { t } = useTranslation()
  const [selectedEventID, setSelectedEventID] = useState<number | undefined>(selectedEvent?.id)
  const [selectedDate, setSelectedDate] = useState<Dayjs | undefined>(selectCalendarDate)

  const isSelectedPrivateCourse = useMemo(() => {
    return selectedCourses.some(course => isPrivateCourseByName(course.name, listPrivateCourse))
  }, [selectedCourses, listPrivateCourse])

  const softBookingEventIdIndex = useMemo(() => {
    const _softBookingEventIdIndex: { [key: string]: number[] } = {}
    listSoftBooking.forEach((booking, i) => {
      if (booking) {
        if (_softBookingEventIdIndex[`E_${booking.event_id}`] !== undefined) {
          _softBookingEventIdIndex[`E_${booking.event_id}`].push(i)
        } else {
          _softBookingEventIdIndex[`E_${booking.event_id}`] = [i]
        }
      }
    })

    return _softBookingEventIdIndex
  }, [listSoftBooking])

  const events = useMemo(() => {
    let _events = eventsData
    _events = JSON.parse(JSON.stringify(_events))

    let selectedTotalPlaces = 0
    selectedCourses.forEach(_selectedCourse => {
      selectedTotalPlaces += _selectedCourse.quantity
    })

    const isAdminOperation = isAdmin && (isAdminMode || isEditBookingMode)
    // Admin can booking over 90 mins.
    const adminBookingTime = -90

    _events = _events.filter(_event => {
      // Get places from softbooking for new calculate limited seats.
      let isEventBookedPrivateCourse = false

      const softbookingKeyId = `E_${_event.id}`
      const softbookingIndex = softBookingEventIdIndex[softbookingKeyId]
      let places = _event.places
      if (softbookingIndex !== undefined && softbookingIndex.length > 0) {
        for (let i=0; i<softbookingIndex.length; i++) {
          const eventSoftBooking = listSoftBooking[softbookingIndex[i]]
          places = places - eventSoftBooking.place

          // Find private course in softbookig.
          if (eventSoftBooking.is_private_course) {
            isEventBookedPrivateCourse = true
          }
        }
      }

      // Find private course in softbookig. (if in softbooking has not).
      if (!isEventBookedPrivateCourse) {
        isEventBookedPrivateCourse = _event.customTickets.some(_ticket => isPrivateCourseByName(_ticket.name, listPrivateCourse) && _ticket.sold > 0)
      }
      
      // #Filter exclude event id
      if (excludeEventIds.indexOf(`${_event.id}`) !== -1) {
        return false
        // Private Course Filter
      } else if (isSelectedPrivateCourse) {
        // #Filter select private course the event have to empty before booking.
        if (places !== _event.maxCapacity) {
          return false
        }
        // #Filter private can booking between 7 days - 60 days before startPreiod.
        const dayStartPeriodDiffNow = dayjs(_event.periods[0].periodStart, 'YYYY-MM-DD HH:mm:ss').diff(dayjs(), 'day')
        if (dayStartPeriodDiffNow < 7 || dayStartPeriodDiffNow > 60) {
          return false
        }
        // #Filter select normal course the event have to not booked by private course.
      } else if (!isSelectedPrivateCourse && isEventBookedPrivateCourse) {
        return false
        // #Filter enough capacitor.
      } else if (places < selectedTotalPlaces) {
        return false
      // #Filter out status cancel
      } else if (_event.status === EventStatusEnum.rejected) {
        return false
      // #Filter out must have 15 minutes before startPeriod ( Filter only custom mode )
      } else if (
        !isAdminOperation &&
        dayjs(_event.periods[0].periodStart, 'YYYY-MM-DD HH:mm:ss').diff(dayjs(), 'minute') < 15
      ) {
        return false
      // #Filter util after startPeriod 30 minutes ( Default )
      } else if (
        isAdminOperation && 
        dayjs(_event.periods[0].periodStart, 'YYYY-MM-DD HH:mm:ss').diff(dayjs(), 'minute') < adminBookingTime
      ) {
        return false
      }
      

      // Filter have course that custom selected.
      let isSelectedCoursesValid = true
      selectedCourses.forEach(_selectedCourse => {
        if (!_event.customTickets.some(_ticket => _ticket.name.toLowerCase() === htmlDecode(_selectedCourse.name).replace('Private: ', '').toLowerCase() && _selectedCourse.price === _ticket.price)) {
          isSelectedCoursesValid = false
        }
      })

      return isSelectedCoursesValid
    })

    return _events
  }, [eventsData, selectedCourses, excludeEventIds, isAdmin, isAdminMode, isEditBookingMode, softBookingEventIdIndex, listSoftBooking, isSelectedPrivateCourse])

  const isFoundAvailableEvent = events.length > 0
  let nearestBookingDate: Dayjs | undefined
  if (events[0]) {
    nearestBookingDate = dayjs(events[0].periods[0].periodStart)
  }

  const displayValidDatesOnCalendar = useCallback((date: Dayjs) => {
    let validDate = false
    events.forEach(_event => {
      if (date.endOf('d').valueOf() === dayjs(_event.periods[0].periodStart, 'YYYY-MM-DD HH:mm:ss').endOf('d').valueOf()) {
        validDate = true
      }
    })

    if (validDate) {
      return false
    }

    return true
  }, [events])

  const eventsWithFilterDateSelected = useMemo(() => {
    const _selectedDate = selectedDate ?? nearestBookingDate
    if (!_selectedDate) {
      return []
    }
    return events.filter(_event => _selectedDate.endOf('d').valueOf() === dayjs(_event.periods[0].periodStart, 'YYYY-MM-DD HH:mm:ss').endOf('d').valueOf())
  }, [events, selectedDate, nearestBookingDate])

  const isBookingPrivateRoom = selectedCourses.length === 1 && selectedCourses.some(course => course.course_type === 'room')

  return (
    <Wrapper id="step-select-date-and-time">
      {
        isEditBookingMode && (
          <div style={{ marginBottom: '16px' }}>
            <strong>{t('booked_courses')}</strong>
            {
              selectedCourses.map((course, i) => {
                return (<div key={i}>{htmlDecode(course.name)} {displayPrice(course.price)} x {course.quantity}</div>)
              })
            }
          </div>
        ) 
      }

      {/* Private Room: Have to display selected room */}
      {
        isBookingPrivateRoom && (
          <div style={{ marginBottom: '16px' }}>
            <strong>{t('private_room_booking')}: </strong><span>{selectedCourses[0].name}</span><br/>
            <strong>{t('number_of_party')}: </strong><span>{selectedCourses[0].number_of_party}</span>
          </div>
        )
      }

      <LabelText>{t('date')}</LabelText>
      <WrapperCalendar disabled={!isFoundAvailableEvent}>
        <Calendar 
          fullscreen={false} 
          disabledDate={displayValidDatesOnCalendar}
          onSelect={(date) => setSelectedDate(date)}
          defaultValue={selectedDate ?? nearestBookingDate}
        />
      </WrapperCalendar>

      {
        !isFoundAvailableEvent && (
          <LabelBoxWrapper>
            <LabelBox type="warning">
              {t('warning_fully_booked_course')}
            </LabelBox>
          </LabelBoxWrapper>
        )
      }

      <ListTimes>
        {
          eventsWithFilterDateSelected.map((_event, i) => {
            const _startTime = dayjs(_event.periods[0].periodStart, 'YYYY-MM-DD HH:mm:ss').format('HH:mm')
            const _endTime = dayjs(_event.periods[0].periodEnd, 'YYYY-MM-DD HH:mm:ss').format('HH:mm')
            return (
              <ListTime key={i}>
                <ListTimeCard 
                  active={selectedEventID === _event.id}
                  onClick={() => {
                    setSelectedEventID(_event.id)
                  }}
                >
                  {_startTime} - {_endTime}
                </ListTimeCard>
              </ListTime>
            )
          })
        }
      </ListTimes>

      {
        isEditBookingMode ? (
          <Footer>
            <div className="yuzu-btn -outline -lg" onClick={() => window.history.back()}><span>{t('back_to_order_details')}</span></div >
            <div className="yuzu-btn -lg" onClick={() => {
              if (isRequestingChangeBooking) {
                return false
              }

              const targetEvent = events.find(_event => _event.id === selectedEventID)
              if (targetEvent) {
                let person = prompt(t('warning_type_confirm_change_booking'));

                if (person === 'Change Booking') {
                  onClickChangeBooking(targetEvent)
                } else {
                  alert(t('warning_correct_typeing_change_booking'))
                }
              } else {
                alert(`${t('warning_must_choose_1_period')}!`)
              }
            }}>
              {
                isRequestingChangeBooking ? (
                  <svg
                    width="30px"
                    height="30px"
                    xmlns="http://www.w3.org/2000/svg"
                    viewBox="0 0 100 100"
                    preserveAspectRatio="xMidYMid"
                    className="lds-rolling"
                  >
                    <circle cx="50" cy="50" fill="none" ng-attr-stroke="{{config.color}}" ng-attr-stroke-width="{{config.width}}" ng-attr-r="{{config.radius}}" ng-attr-stroke-dasharray="{{config.dasharray}}" stroke="#fff" strokeWidth="10" r="35" strokeDasharray="164.93361431346415 56.97787143782138" transform="rotate(162 50 50)"><animateTransform attributeName="transform" type="rotate" calcMode="linear" values="0 50 50;360 50 50" keyTimes="0;1" dur="400ms" begin="0s" repeatCount="indefinite"></animateTransform></circle>
                  </svg>
                ) : (
                    <span>{t('update_booking')}</span>
                )
              }
            </div>
          </Footer>
        ) : (
          <Footer>
            <div 
              className="yuzu-btn -outline -lg" 
              onClick={() => {
                if (isBookingPrivateRoom) {
                  onClickBackPrivateRoomBooking()
                } else {
                  onClickBack()
                }
              }}>
                <span>{t('back')}</span>
            </div>
            <div className="yuzu-btn -lg" onClick={() => {
              const targetEvent = events.find(_event => _event.id === selectedEventID)
              if (targetEvent) {
                onClickNext(targetEvent)
              } else {
                alert(`${t('warning_must_choose_1_period')}!`)
              }
            }}><span>{t('next')}</span></div>
          </Footer >
        )
      }
    </Wrapper>
  )
}

export default SelectCourseDate

const Wrapper = styled.div`
  max-width: 500px;
`

const LabelBoxWrapper = styled.div`
  margin-bottom: 24px;
`

const WrapperCalendar = styled.div<{ disabled: boolean }>`
  margin-bottom: 24px;

  .ant-picker-cell-inner {
    max-width: 64px;
    width: 100%;

    @media (max-width: 767px) {
      max-width: 42px;
    }
  }
  
  table.ant-picker-content th,
  table.ant-picker-content td {
    border: 0 !important;
  }

  ${({ disabled }) => disabled && `
    .ant-picker-cell-selected .ant-picker-cell-inner {
      background-color: #DDD !important;
      color: rgba(0, 0, 0, 0.5) !important;
    }
  `}
`

const Footer = styled.div`
  margin-top: 64px;
  margin-bottom: 80px;
  display: flex;
  justify-content: space-between;
`

const ListTimes = styled.div`
  display: flex;
  flex-wrap: wrap;
  margin-left: -8px;
  margin-right: -8px;
  margin-bottom: -16px;
`

const ListTime = styled.div`
  padding: 0 8px;
  margin-bottom: 16px;
  width: 50%;

  @media (max-width: 767px) {
    margin-bottom: 8px;
  }
`

const ListTimeCard = styled.div<{ active: boolean }>`
  width: 100%;
  border-radius: 8px;
  box-shadow: inset 0 0 0 1px var(--global--color-background-secondary);
  height: 40px;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 14px;
  color: var(--global--color-background-secondary);
  font-weight: bold;
  cursor: pointer;

  ${({ active }) => active && `
    color: white;
    background-color: var(--global--color-background-secondary);
  `}
`